































import { computed, defineComponent, PropType, useRouter } from '@nuxtjs/composition-api';
// Type-only (erased at runtime) so the component barrel <-> composable import is not a runtime cycle.
import type { ISearchPreFiltersController } from '@shared/composables/useSearchPreFilters';
import { UCIcon } from '@shared/ui';

import SearchPreFilters from './SearchPreFilters.vue';
import { ISearchPreFiltersValue } from './types';

// Drop-in integration surface for the pre-filter engine: renders the Filters trigger + dialog and
// owns all the glue (commit context, close, navigate, clear). A brand wires it with one tag, passing
// its own `useSearchPreFilters()` singleton as `controller`.
export default defineComponent({
  name: 'SearchPreFiltersControl',
  components: {
    SearchPreFilters,
    UCIcon,
  },
  props: {
    // The brand's useSearchPreFilters() singleton (created via createSearchPreFilters).
    controller: {
      type: Object as PropType<ISearchPreFiltersController>,
      required: true,
    },
    // Current free-text term; merged into the results URL when the user hits the modal's Search.
    searchValue: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const router = useRouter();

    const open = computed(() => props.controller.showPreFilters.value);
    const config = computed(() => props.controller.searchPreFiltersConfig);
    const value = computed(() => props.controller.selectedFilters.value);
    const facetOptions = computed(() => props.controller.facetOptions.value);
    const loading = computed(() => props.controller.loading.value);
    const activeFilterCount = computed(() => props.controller.activeFilterCount.value);

    const openDialog = () => props.controller.openPreFilters();
    const onClose = () => props.controller.closePreFilters();
    const onClear = () => props.controller.clearFilters();

    // Commit on any modal interaction so the context applies even if closed with the X.
    const onChange = (filters: ISearchPreFiltersValue, activeTabIndex: number) =>
      props.controller.commitContext(filters, activeTabIndex);

    const onSearch = (filters: ISearchPreFiltersValue, activeTabIndex: number) => {
      props.controller.commitContext(filters, activeTabIndex);
      props.controller.closePreFilters();
      router.push(props.controller.buildContextualSearchQuery(props.searchValue));
      // URL already carries the filters; reset the in-memory UI state (modal + badge).
      props.controller.clearFilters();
    };

    return {
      open,
      config,
      value,
      facetOptions,
      loading,
      activeFilterCount,
      openDialog,
      onClose,
      onClear,
      onChange,
      onSearch,
    };
  },
});
